import { OrganizationIntegrationAssignAccountsBatchBody } from "@common/schemas/organization-integration.schema";
import { pluralizeWithCount } from "@common/helpers/string";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import {
    ASSIGN_BATCH_MAX,
    useAssignOrgIntegrationAccountsBatchMutation,
} from "@api/organizationIntegration.api";
import { useOrganizationIntegrations } from "./OrganizationIntegrationsContext";

type Assignments =
    OrganizationIntegrationAssignAccountsBatchBody["assignments"];

/**
 * Why a batch this size can't be sent, if it can't. Surfaced up front rather
 * than discovered through a rejected request.
 */
export const assignmentCapReason = (
    accountCount: number,
): string | undefined =>
    accountCount > ASSIGN_BATCH_MAX
        ? `Assign at most ${ASSIGN_BATCH_MAX} accounts at a time.`
        : undefined;

interface CommitOptions {
    /** Receives the accounts that actually persisted, if any did. */
    onAssigned?: (accountIds: number[]) => void;
    /** Runs when the request succeeds (HTTP 200), including partial failures. */
    onSuccess?: () => void;
    /** Runs once the request settles, whether it succeeded or not. */
    onSettled?: () => void;
}

/**
 * Commits staged client/entity assignments and reports the outcome.
 *
 * A 200 can still carry per-account failures, so the response is always split
 * into what landed and what didn't: successful rows drop their staged pick and
 * refresh the table, failed rows keep theirs so the user can retry without
 * re-picking. Shared by the single-row and bulk entry points so the two can't
 * drift on which toast the user sees.
 *
 * These are mutate-scoped callbacks, which react-query drops if the caller
 * unmounts before the request settles. Callers must therefore stay mounted
 * until `onSettled` runs, or the outcome goes unreported.
 */
export const useCommitAssignments = () => {
    const { organizationId, clearPendingAccounts, onAssignmentsCommitted } =
        useOrganizationIntegrations();
    const { error: errorToast, success: successToast } = useToaster();
    const assignMutation =
        useAssignOrgIntegrationAccountsBatchMutation(organizationId);

    const commit = (assignments: Assignments, options: CommitOptions = {}) => {
        assignMutation.mutate(assignments, {
            onSuccess: ({ updated, failed }) => {
                const assignedAccountIds = updated.map(
                    (assignment) => assignment.financialAccountId,
                );
                clearPendingAccounts(assignedAccountIds);

                if (failed.length > 0) {
                    const reasons = [
                        ...new Set(failed.map((failure) => failure.error)),
                    ].join(" ");
                    errorToast(
                        `Couldn't update ${pluralizeWithCount("account", failed.length)}. ${reasons}`,
                    );
                } else {
                    // The same request both assigns a fresh account and moves
                    // an assigned one to another entity, so the toast has to
                    // read correctly either way.
                    successToast(
                        `Updated ${pluralizeWithCount("account", updated.length)}`,
                    );
                }

                if (assignedAccountIds.length > 0) {
                    options.onAssigned?.(assignedAccountIds);
                    onAssignmentsCommitted();
                }

                options.onSuccess?.();
            },
            // A rejected request is already toasted by the shared axios
            // interceptor, and staging is deliberately left intact so the user
            // can retry without re-picking every row.
            onSettled: options.onSettled,
        });
    };

    return { commit, isCommitting: assignMutation.isPending };
};
